import { Box, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";

import theme from "@/theme";

import DiagnosisPhone from "@assets/image/registration_v2/phone-diagnosis.png";
import LearningPhone from "@assets/image/registration_v2/phone-learning.png";
import PlannerPhone from "@assets/image/registration_v2/phone-planner.png";
import PropertyPhone from "@assets/image/registration_v2/phone-property.png";

const STEPS = [
  {
    image: DiagnosisPhone,
    message: "登録情報から、\nあなたの投資力がわかる",
    description: "カンタン3ステップ。まずはあなたに合った\n不動産投資の進め方を診断します",
  },
  {
    image: PlannerPhone,
    message: "認定プランナーを\n選んで話が聞ける",
    description: "相性の合うプランナーをご紹介。\n話を聞く相手は、自分で選べます",
  },
  {
    image: LearningPhone,
    message: "はじめての方でも\nじっくり学べる",
    description: "基礎から実践まで全3段階。\n1章5分ほどで読み進められます",
  },
  {
    image: PropertyPhone,
    message: "保有中・検討中の物件の\n価値がすぐわかる",
    description: "毎月の収支もローンの残高も、\n登録すれば自動で計算されます",
  },
];
const SLIDES = [STEPS[STEPS.length - 1], ...STEPS, STEPS[0]];
const DURATION = 450;
const activeIndex = (position: number) => (position - 1 + STEPS.length) % STEPS.length;

const useStyles = makeStyles(() => ({
  root: { width: "100%", minWidth: 0 },
  viewport: { width: "100%", overflow: "hidden", touchAction: "pan-y" },
  track: {
    display: "flex",
    alignItems: "stretch",
    willChange: "transform",
    transition: `transform ${DURATION}ms cubic-bezier(0.22, 1, 0.36, 1)`,
    "@media (prefers-reduced-motion: reduce)": { transition: "none", willChange: "auto" },
  },
  slide: {
    flex: "0 0 100%",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  heading: {
    fontSize: 20,
    lineHeight: 1.3,
    fontWeight: 700,
    whiteSpace: "pre-line",
    [theme.breakpoints.up("md")]: { fontSize: 24 },
  },
  description: {
    fontSize: 14,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    whiteSpace: "pre-line",
    marginTop: 8,
  },
  mock: {
    width: "100%",
    maxWidth: 274,
    height: 275,
    borderRadius: 24,
    position: "relative",
    overflow: "hidden",
    marginTop: 24,
    background: `linear-gradient(32deg, ${theme.palette.primary.main}, ${theme.palette.semantic["chart/income-2"]})`,
  },
  phone: { width: 220, height: "auto", position: "absolute", top: 15, left: "50%", transform: "translateX(-50%)" },
  dots: { display: "flex", justifyContent: "center", gap: 12, marginTop: 16 },
  dot: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    border: 0,
    padding: 0,
    cursor: "pointer",
    backgroundColor: theme.palette.common.white,
    transition: "background-color 200ms ease",
    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
    [theme.breakpoints.up("md")]: {
      "&[aria-pressed='false']": { backgroundColor: theme.palette.semantic["border/default"] },
    },
  },
  active: { backgroundColor: theme.palette.primary.main },
}));

export const OnboardingCarousel: React.FC = () => {
  const classes = useStyles();
  const [slide, setSlide] = React.useState({ position: 1, animate: true });
  const touchStartRef = React.useRef<{ x: number; y: number } | null>(null);
  const index = activeIndex(slide.position);
  const goNext = React.useCallback(() => {
    setSlide((current) => ({ position: activeIndex(current.position) + 2, animate: true }));
  }, []);
  const goPrev = React.useCallback(() => {
    setSlide((current) => ({ position: activeIndex(current.position), animate: true }));
  }, []);
  const finishTransition = React.useCallback(() => {
    setSlide((current) => {
      if (current.position !== 0 && current.position !== STEPS.length + 1) return current;
      return { position: activeIndex(current.position) + 1, animate: false };
    });
  }, []);

  React.useEffect(() => {
    if (index === STEPS.length - 1) return undefined;
    const timer = window.setTimeout(goNext, 4000);
    return () => window.clearTimeout(timer);
  }, [goNext, index]);

  // The adjacent duplicate makes either wrap a single-slide movement. Reset
  // without animation once it settles; the timer also covers reduced motion.
  React.useEffect(() => {
    if (slide.position !== 0 && slide.position !== STEPS.length + 1) return undefined;
    const timer = window.setTimeout(finishTransition, DURATION + 50);
    return () => window.clearTimeout(timer);
  }, [finishTransition, slide.position]);

  return (
    <Box
      className={classes.root}
      role="region"
      aria-label="Propallyの機能紹介"
      onTouchStart={(event) => {
        const touch = event.touches.length === 1 ? event.touches[0] : null;
        touchStartRef.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
      }}
      onTouchEnd={(event) => {
        const start = touchStartRef.current;
        touchStartRef.current = null;
        const end = event.changedTouches[0];
        if (!start || !end) return;
        const x = end.clientX - start.x;
        const y = end.clientY - start.y;
        if (Math.abs(x) < 40 || Math.abs(x) <= Math.abs(y)) return;
        if (x < 0) goNext();
        else goPrev();
      }}
      onTouchCancel={() => {
        touchStartRef.current = null;
      }}
    >
      <Box className={classes.viewport}>
        <Box
          className={classes.track}
          data-onboarding-track
          style={{ transform: `translateX(-${slide.position * 100}%)`, transition: slide.animate ? undefined : "none" }}
          onTransitionEnd={(event) => {
            if (event.target === event.currentTarget && event.propertyName === "transform") finishTransition();
          }}
        >
          {SLIDES.map((step, position) => {
            const isActive = position > 0 && position <= STEPS.length && position - 1 === index;
            return (
              <Box key={position} className={classes.slide} aria-hidden={!isActive}>
                <Typography component={isActive ? "h1" : "p"} className={classes.heading}>
                  {step.message}
                </Typography>
                <Typography className={classes.description}>{step.description}</Typography>
                <Box className={classes.mock}>
                  <img src={step.image} alt="" className={classes.phone} />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
      <Box className={classes.dots}>
        {STEPS.map((_, step) => (
          <button
            type="button"
            key={step}
            aria-label={`step_${step + 1}`}
            aria-pressed={step === index}
            className={`${classes.dot} ${step === index ? classes.active : ""}`}
            onClick={() => setSlide({ position: step + 1, animate: true })}
          />
        ))}
      </Box>
    </Box>
  );
};
